import { useEffect } from 'react';

const PUBLIC_PAGE_CLASS = 'realm-public-page';
const HASH_ANCHOR_OBSERVER_TIMEOUT_MS = 5000;

/** usePublicPageViewport 标记官网页面作用域，供公开页面使用独立的响应式样式与行为。 */
export function usePublicPageViewport() {
  useEffect(() => {
    document.documentElement.classList.add(PUBLIC_PAGE_CLASS);
    return () => document.documentElement.classList.remove(PUBLIC_PAGE_CLASS);
  }, []);
}

/** scrollToPublicPageHash 定位指定 hash 指向的公开页区块，并容忍非法编码。 */
function scrollToPublicPageHash(hash: string): boolean {
  if (!hash) return true;
  let targetID: string;
  try {
    targetID = decodeURIComponent(hash.slice(1));
  } catch {
    return true;
  }
  const target = document.getElementById(targetID);
  if (!target) return false;
  target.scrollIntoView();
  return true;
}

/** usePublicPageHashAnchor 在异步 Schema 落入 DOM 后按每次 Router 导航恢复同页锚点定位。 */
export function usePublicPageHashAnchor(readyKey: string | undefined, hash: string, navigationKey: string) {
  useEffect(() => {
    if (!readyKey || !hash) return undefined;
    let observer: MutationObserver | undefined;
    let stopTimer: number | undefined;

    /** stopObserving 统一释放全页观察器和超时任务，避免无效 hash 长期扫描动态页面。 */
    const stopObserving = () => {
      observer?.disconnect();
      observer = undefined;
      if (stopTimer !== undefined) {
        window.clearTimeout(stopTimer);
        stopTimer = undefined;
      }
    };

    /** locateCurrentHash 优先立即定位，目标尚未渲染时只观察到首次命中。 */
    const locateCurrentHash = () => {
      stopObserving();
      if (scrollToPublicPageHash(hash)) return;
      // DynamicView 可能仍处于 Suspense；目标出现后立即停止观察，无效 hash 到期也主动释放。
      observer = new MutationObserver(() => {
        if (scrollToPublicPageHash(hash)) {
          stopObserving();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
      stopTimer = window.setTimeout(stopObserving, HASH_ANCHOR_OBSERVER_TIMEOUT_MS);
    };

    locateCurrentHash();
    return stopObserving;
  }, [hash, navigationKey, readyKey]);
}
